import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { ApWireRecord } from '@discover/data-models/records/ap-wire-record';
import { WireFilter, WireSearchAPIResponse } from '@discover/store/models/discover.models';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { MultipleFeedViewStateRecord } from '@src/suite/common/data-models/records/mutiple-feed-viewstate-record';
import { SingleFeedViewStateRecord } from '@src/suite/common/data-models/records/single-feed-viewstate-record';
import { TwitterFeedViewStateRecord } from '@src/suite/common/data-models/records/twitter-feed-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { WireSearchViewStateRecord } from '@src/suite/common/data-models/records/wire-search-viewstate-record';
import { WireServiceRecord } from '@src/suite/common/data-models/service/wire-service-record';
import { ViewFeedTypes } from '@src/suite/common/enums/enums';
import { from, map, Observable, of, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DiscoverSearchService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService
    ) {}

    private mapFeedType(type: ViewStateTypes): string {
        if (type === ViewStateTypes.RSSFEED) return ViewFeedTypes.RSS;
        if (type === ViewStateTypes.TWITTERFEED) return ViewFeedTypes.TWITTER;
        if (type === ViewStateTypes.WIREFEED) return ViewFeedTypes.AP;
        if (type === ViewStateTypes.APFEED) return ViewFeedTypes.AP;
        if (type === ViewStateTypes.FTPFEED) return ViewFeedTypes.FTP;
        return null;
    }

    public searchWires(vs: ViewStateRecord, filters: Array<FilterDef>): Observable<WireSearchAPIResponse> {
        const URL: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wires/v2/search';
        const searchTerm: string = vs.data.PSTViewStateSearchQuery;
        const filtersPayload = this.getFilterPayload(vs, filters);
        try {
            return this.nomWebServicesJson
                .discoverServicePost<WireSearchAPIResponse>(
                    URL,
                    {
                        page: 0,
                        pageSize: 200,
                        search: searchTerm,
                        filters: filtersPayload,
                    },
                    {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                            Authorization: this.nomWebServicesJson.token,
                            accept: 'text/plain',
                        }),
                        responseType: 'json',
                        observe: 'response',
                    }
                )
                .pipe(
                    map(res => {
                        return {
                            totalCount: res?.body?.totalCount || 0,
                            results: (res?.body?.results || []).map(item => {
                                return this.processWire(item);
                            }),
                        };
                    })
                );
        } catch (error) {
            return throwError(() => error);
        }
    }

    public loadWires(vs: ViewStateRecord, filters: Array<FilterDef>): Observable<Array<StorytellingWireRecord>> {
        if (!vs) return of([]);
        let response: Observable<Array<StorytellingWireRecord>> = null;
        const feedURL: string = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wires/v2/filter';
        const payload = this.getFilterPayload(vs, filters);
        try {
            response = from(
                this.nomWebServicesJson.discoverServicePost<StorytellingWireRecord[]>(
                    feedURL,
                    {
                        filter: payload,
                    },
                    {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                            Authorization: this.nomWebServicesJson.token,
                            accept: 'text/plain',
                        }),
                        responseType: 'json',
                        observe: 'response',
                    }
                )
            ).pipe(
                map(res => {
                    return (res.body || []).map(item => {
                        return this.processWire(item);
                    });
                })
            );
        } catch (error) {
            return throwError(() => error);
        }

        return response;
    }

    private getFilterPayload(vs: ViewStateRecord, filters: Array<FilterDef>): Array<WireFilter> {
        const payload: Array<WireFilter> = [];
        let publishedDate: string = null;
        let providerId: string = null;
        let category: string = null;
        if (vs instanceof SingleFeedViewStateRecord || vs instanceof WireSearchViewStateRecord) {
            for (const filter of filters) {
                if (filter.NotUsed) {
                    // neither in the required nor in the optional list
                    continue;
                }
                if (filter.ID === PSTFieldNames.PSTWirePublished) {
                    const value = filter.get();
                    if (value) publishedDate = value.FieldValue;
                }
                if (filter.ID === PSTFieldNames.PSTWireProvider) {
                    const value = filter.get();
                    if (value) providerId = value.FieldValue;
                }
                if (filter.ID === PSTFieldNames.PSTWireCategory) {
                    const value = filter.get();
                    if (value) category = value.FieldValue;
                }
            }
        }

        if (
            vs.viewStateType === ViewStateTypes.RSSFEED ||
            vs.viewStateType === ViewStateTypes.APFEED ||
            vs.viewStateType === ViewStateTypes.FTPFEED ||
            vs.viewStateType === ViewStateTypes.WIREFEED ||
            vs.viewStateType === ViewStateTypes.WIRESEARCH
        ) {
            let filter: WireFilter = {
                feedType: this.mapFeedType(vs.viewStateType),
                feedId: (vs as SingleFeedViewStateRecord).viewStateFeedID,
                count: 200,
            };
            filter = publishedDate ? { ...filter, publishedDate: publishedDate } : filter;
            filter = providerId ? { ...filter, providerId: providerId } : filter;
            filter = category ? { ...filter, category: category } : filter;
            payload.push(filter);
        }

        if (vs.viewStateType === ViewStateTypes.TWITTERFEED && vs instanceof TwitterFeedViewStateRecord) {
            let viewStateFeedIds = null;
            try {
                viewStateFeedIds = JSON.parse(vs.viewStateFeedID);
            } catch (error) {
                if (vs.viewStateFeedID?.trim()) {
                    viewStateFeedIds = [vs.viewStateFeedID.trim()];
                }
            }

            (viewStateFeedIds || []).forEach(feedId => {
                let filter: WireFilter = {
                    feedType: this.mapFeedType(vs.viewStateType),
                    feedId: feedId,
                    count: 200,
                };
                filter = publishedDate ? { ...filter, publishedDate: publishedDate } : filter;
                filter = providerId ? { ...filter, providerId: providerId } : filter;
                filter = category ? { ...filter, category: category } : filter;
                payload.push(filter);
            });
        }

        if (vs.viewStateType === ViewStateTypes.MULTIPLEFEED && vs instanceof MultipleFeedViewStateRecord) {
            if (vs.viewStates && vs.viewStates.length > 0) {
                vs.viewStates.forEach(item => {
                    let filter: WireFilter = {
                        feedType: this.mapFeedType(item.viewStateType),
                        feedId: (item as SingleFeedViewStateRecord).viewStateFeedID,
                        count: 200,
                    };
                    filter = publishedDate ? { ...filter, publishedDate: publishedDate } : filter;
                    filter = providerId ? { ...filter, providerId: providerId } : filter;
                    filter = category ? { ...filter, category: category } : filter;
                    payload.push(filter);
                });
            }
        }
        return payload;
    }

    private processWire(wire: any): StorytellingWireRecord {
        const mediaLink = wire?.properties?.MediaLinks?.trim();
        if (mediaLink) {
            try {
                const parsedMediaLinks = JSON.parse(mediaLink);
                if (Array.isArray(parsedMediaLinks)) {
                    if (parsedMediaLinks.length > 0 && parsedMediaLinks[0]?.trim()) {
                        wire.properties.MediaLinks = parsedMediaLinks[0];
                    } else {
                        wire.properties.MediaLinks = null;
                    }
                } else {
                    wire.properties.MediaLinks = mediaLink;
                }
            } catch (error) {
                wire.properties.MediaLinks = mediaLink;
            }
        }
        try {
            const wireServiceRecord: WireServiceRecord = WireServiceRecord.instance(wire);
            const wireRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingWireRecord>(wireServiceRecord);
            return wireRecord;
        } catch (error) {
            console.log(error);
        }
        return null;
    }

    public async getApWireById(wireId: string): Promise<ServiceResponse<ApWireRecord>> {
        let response: HttpResponse<any> = null;
        const wireURL = this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wires/' + wireId;
        try {
            response = await this.nomWebServicesJson.discoverAPIGet(wireURL, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    Authorization: this.nomWebServicesJson.token,
                    accept: 'application/json',
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load wire with Id=' + wireId + '!',
                detailMessage: `Failed to load wire! \n\rStackTrace: \n\rError: ${error}`,
            });
            return new ServiceResponse<ApWireRecord>(false, response.body);
        }
        return new ServiceResponse<ApWireRecord>(true, response.body);
    }
}
